import {
  buildWebsiteSearchAction,
  type WebsiteSearchActionBlok,
} from "./WebsiteStructuredDataHelpers";

const formatStructuredDataRating = (note: number) =>
  (Math.round(note * 10) / 10).toFixed(1);

type AddressBlok = {
  street_address?: string;
  postal_code?: string;
  address_locality?: string;
  address_country?: string;
};

type ContactPointBlok = {
  telephone?: string;
  email?: string;
  contact_type?: string;
};

type OrganizationBlok = {
  name?: string;
  description?: string;
  logo?: { filename?: string };
  email?: string;
  address?: AddressBlok[];
  contact_point?: ContactPointBlok[];
};

type WebsiteBlok = WebsiteSearchActionBlok & {
  name?: string;
  description?: string;
  about?: string;
};

type ReviewsBlok = {
  component?: string;
  body?: ReviewsBlok[];
  note?: number;
  base_rating?: number;
  total_reviews?: number;
};

type HeaderContent = {
  organization?: OrganizationBlok[];
  website?: WebsiteBlok[];
};

type PageContent = {
  body?: ReviewsBlok[];
};

const normalizeStructuredDataText = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || undefined;
  }

  if (!value || typeof value !== "object") return undefined;

  const node = value as {
    text?: string;
    content?: unknown[];
  };

  if (typeof node.text === "string") {
    const trimmed = node.text.trim();
    return trimmed || undefined;
  }

  if (Array.isArray(node.content)) {
    const text = node.content
      .map(normalizeStructuredDataText)
      .filter((part): part is string => !!part)
      .join(" ")
      .trim();

    return text || undefined;
  }

  return undefined;
};

const findBlokByComponent = (
  bloks: ReviewsBlok[] | undefined,
  component: string
): ReviewsBlok | null => {
  if (!bloks?.length) return null;

  for (const blok of bloks) {
    if (blok.component === component) return blok;

    const nested = findBlokByComponent(blok.body, component);
    if (nested) return nested;
  }

  return null;
};

const buildPostalAddress = (addressBlok: AddressBlok | undefined) => {
  if (!addressBlok) return null;

  const { street_address, postal_code, address_locality, address_country } =
    addressBlok;

  if (
    !street_address &&
    !postal_code &&
    !address_locality &&
    !address_country
  ) {
    return null;
  }

  return {
    "@type": "PostalAddress",
    ...(street_address && { streetAddress: street_address }),
    ...(postal_code && { postalCode: postal_code }),
    ...(address_locality && { addressLocality: address_locality }),
    ...(address_country && { addressCountry: address_country }),
  };
};

const buildContactPoints = (contactPoints: ContactPointBlok[] | undefined) => {
  if (!contactPoints?.length) return null;

  const points = contactPoints
    .map((contact) => {
      if (!contact.telephone && !contact.email) return null;

      return {
        "@type": "ContactPoint",
        ...(contact.contact_type && { contactType: contact.contact_type }),
        ...(contact.telephone && { telephone: contact.telephone }),
        ...(contact.email && { email: contact.email }),
      };
    })
    .filter((contact): contact is NonNullable<typeof contact> => contact !== null);

  return points.length ? points : null;
};

const getHomepageAggregateRating = (
  pageContent: PageContent | null | undefined
) => {
  const reviewsBlok =
    findBlokByComponent(pageContent?.body, "facility-reviews") ??
    findBlokByComponent(pageContent?.body, "illustrated-rating");

  if (!reviewsBlok?.note || !reviewsBlok.total_reviews) return null;

  const ratingValue = formatStructuredDataRating(reviewsBlok.note);

  return {
    "@type": "AggregateRating",
    ratingValue,
    reviewCount: String(reviewsBlok.total_reviews),
    ...(reviewsBlok.base_rating && {
      bestRating: String(reviewsBlok.base_rating),
    }),
  };
};

const buildOrganizationNode = (
  orgBlok: OrganizationBlok | undefined,
  pageContent: PageContent | null | undefined,
  baseURL: string
) => {
  const address = buildPostalAddress(orgBlok?.address?.[0]);
  const contactPoint = buildContactPoints(orgBlok?.contact_point);
  const aggregateRating = getHomepageAggregateRating(pageContent);

  return {
    "@type": "Organization",
    "@id": `${baseURL}/#organization`,
    url: baseURL,
    ...(orgBlok?.name && { name: orgBlok.name }),
    ...(orgBlok?.logo?.filename && { logo: orgBlok.logo.filename }),
    ...(orgBlok?.description && { description: orgBlok.description }),
    ...(orgBlok?.email && { email: orgBlok.email }),
    ...(address && { address }),
    ...(contactPoint && { contactPoint }),
    ...(aggregateRating && { aggregateRating }),
  };
};

const buildWebsiteNode = (
  websiteBlok: WebsiteBlok | undefined,
  baseURL: string
) => {
  const searchAction = buildWebsiteSearchAction(websiteBlok?.search_action);

  return {
    "@type": "WebSite",
    "@id": `${baseURL}/#website`,
    url: baseURL,
    publisher: {
      "@id": `${baseURL}/#organization`,
    },
    about: websiteBlok?.about ?? null,
    ...(websiteBlok?.name && { name: websiteBlok.name }),
    ...(websiteBlok?.description && { description: websiteBlok.description }),
    ...(searchAction && { potentialAction: searchAction }),
  };
};

export const buildHomepageStructuredData = (
  headerContent: HeaderContent | null | undefined,
  pageContent: PageContent | null | undefined,
  baseURL: string
) => {
  const orgBlok = headerContent?.organization?.[0];
  const websiteBlok = headerContent?.website?.[0];

  return {
    "@context": "https://schema.org",
    "@graph": [
      buildOrganizationNode(orgBlok, pageContent, baseURL),
      buildWebsiteNode(websiteBlok, baseURL),
    ],
  };
};
